import { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Upload,
    Popconfirm,
    Select,
    message,
} from 'antd';
import {DeleteOutlined, EditOutlined, UploadOutlined} from '@ant-design/icons';
import PortfolioApi from '../api/portfolio';
import LangApi from '../api/langs';

// ...все импорты остаются без изменений

const Portfolio = () => {
    const [list, setList] = useState([]);
    const [langs, setLangs] = useState([]);
    const [selectedLang, setSelectedLang] = useState('ru');
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [coverFile, setCoverFile] = useState([]);
    const [slideFiles, setSlideFiles] = useState([]);
    const [currentEdit, setCurrentEdit] = useState(null);
    const BASE_URL = process.env.REACT_APP_URL;

    const fetchLangs = async () => {
        const res = await LangApi.getAll();
        setLangs(res.data);
    };

    const fetchAll = async () => {
        setLoading(true);
        const res = await PortfolioApi.getByLocale(selectedLang);
        setList(res.data);
        setLoading(false);
    };

    useEffect(() => {
        fetchLangs();
    }, []);

    useEffect(() => {
        fetchAll();
    }, [selectedLang]);

    const handleSubmit = async (values) => {
        try {
            const formData = new FormData();
            formData.append('title', values.title);
            formData.append('short_desc', values.short_desc);
            formData.append('description', values.description);
            formData.append('lang_id', values.lang_id);

            if (coverFile[0]?.originFileObj) {
                formData.append('files', coverFile[0].originFileObj);
                formData.append('cover_originalname', coverFile[0].name);
            } else if (currentEdit?.cover_image) {
                formData.append('cover_image', currentEdit.cover_image); // сохранить старую обложку
            }

            slideFiles.forEach((f) => {
                if (f.originFileObj) {
                    formData.append('files', f.originFileObj);
                }
            });

            if (currentEdit) {
                await PortfolioApi.update(currentEdit.id, formData);
                message.success('Обновлено');
            } else {
                await PortfolioApi.create(formData);
                message.success('Добавлено');
            }

            closeModal();
            fetchAll();
        } catch {
            message.error('Ошибка при сохранении');
        }
    };

    const handleDelete = async (id) => {
        await PortfolioApi.remove(id);
        fetchAll();
        message.success('Удалено');
    };

    const openModal = (record = null) => {
        form.resetFields();
        setCoverFile([]);
        setSlideFiles([]);
        setCurrentEdit(record);

        if (record) {
            form.setFieldsValue({
                lang_id: record.lang.id,
                title: record.title,
                short_desc: record.short_desc,
                description: record.description,
            });

            if (record.cover_image) {
                setCoverFile([
                    {
                        uid: '-1',
                        name: 'cover.jpg',
                        status: 'done',
                        url: `${BASE_URL}${record.cover_image}`,
                    },
                ]);
            }

            if (record.slides?.length) {
                const initialSlides = record.slides.map((slide, index) => ({
                    uid: `-${index + 2}`,
                    name: slide.file_path.split('/').pop(),
                    status: 'done',
                    url: `${BASE_URL}${slide.file_path}`,
                }));
                setSlideFiles(initialSlides);
            }
        }

        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setCurrentEdit(null);
        setCoverFile([]);
        setSlideFiles([]);
    };

    const columns = [
        {
            title: 'Обложка',
            dataIndex: 'cover_image',
            render: (img) => img && <img src={`${BASE_URL}${img}`} className="w-24 h-16 object-cover" />,
        },
        { title: 'Название', dataIndex: 'title' },
        { title: 'Описание', dataIndex: 'short_desc' },
        {
            title: 'Слайды',
            dataIndex: 'slides',
            render: (slides) => (
                <div className="flex gap-2 flex-wrap">
                    {slides.map((s, i) =>
                        s.file_path.endsWith('.mp4') ? (
                            <video key={i} src={`${BASE_URL}${s.file_path}`} className="w-24 h-16" controls />
                        ) : (
                            <img key={i} src={`${BASE_URL}${s.file_path}`} className="w-24 h-16 object-cover" />
                        )
                    )}
                </div>
            ),
        },
        {
            title: 'Действия',
            render: (_, record) => (
                <div className="flex gap-2">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openModal(record)}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        Редактировать
                    </Button>

                    <Popconfirm title="Удалить?" onConfirm={() => handleDelete(record.id)}>
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            className="hover:bg-red-600 hover:text-white"
                        >
                            Удалить
                        </Button>
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Портфолио</h2>
                <div className="flex gap-2">
                    <Select
                        value={selectedLang}
                        onChange={setSelectedLang}
                        options={langs.map((l) => ({ label: l.name, value: l.code }))}
                        style={{ width: 160 }}
                    />
                    <Button type="primary" onClick={() => openModal()}>Добавить</Button>
                </div>
            </div>

            <Table
                dataSource={list}
                columns={columns}
                rowKey="id"
                loading={loading}
            />

            <Modal
                title={currentEdit ? 'Редактировать портфолио' : 'Добавить портфолио'}
                open={modalVisible}
                onCancel={closeModal}
                onOk={() => form.submit()}
                okText="Сохранить"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="lang_id" label="Язык" rules={[{ required: true }]}>
                        <Select options={langs.map((l) => ({ label: l.name, value: l.id }))} />
                    </Form.Item>
                    <Form.Item name="title" label="Заголовок" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="short_desc" label="Краткое описание" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Полное описание" rules={[{ required: true }]}>
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item label="Обложка">
                        <Upload
                            beforeUpload={() => false}
                            fileList={coverFile}
                            maxCount={1}
                            listType="picture"
                            onChange={({ fileList }) => setCoverFile(fileList)}
                            accept="image/*"
                        >
                            <Button icon={<UploadOutlined />}>Загрузить обложку</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item label="Слайды (изображения или видео)">
                        <Upload
                            multiple
                            beforeUpload={() => false}
                            fileList={slideFiles}
                            onChange={({ fileList }) => setSlideFiles(fileList)}
                            accept="image/*,video/*"
                        >
                            <Button icon={<UploadOutlined />}>Загрузить слайды</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Portfolio;
